import { telemetryAttemptId, walletTelemetry } from "@mudwater/sdk";

import {
  startIdentityTrace,
  identityTracePhase,
} from "@/lib/wallet/identity-trace";
import { rumEvent } from "@/lib/faro";

import type { WalletTelemetryInput } from "@mudwater/sdk";

export type IdentityAttempt = Record<string, string>;

/** Descriptor-only reads never invoke a wallet's getters or serialize its payload. */
function field(value: unknown, key: string): unknown {
  try {
    if (
      (typeof value !== "object" && typeof value !== "function") ||
      value === null
    )
      return undefined;
    return Object.getOwnPropertyDescriptor(value, key)?.value;
  } catch {
    return undefined;
  }
}

const CODES = new Set([
  "ACTION_REJECTED",
  "SIGN_IN_NOT_RECORDED",
  "WALLET_NOT_READY",
  "SIGN_IN_RESPONSE_INVALID",
  "SIGN_IN_UNAVAILABLE",
  "AbortError",
  "TimeoutError",
]);
function errorName(value: unknown): unknown {
  try {
    // Use the browser's branded getter, never an accessor supplied by the wallet.
    if (typeof DOMException !== "undefined" && value instanceof DOMException) {
      const name: unknown = Object.getOwnPropertyDescriptor(
        DOMException.prototype,
        "name"
      )?.get?.call(value);
      if (typeof name === "string") return name;
    }
    for (
      let depth = 0;
      value && typeof value === "object" && depth < 4;
      depth += 1
    ) {
      const name = field(value, "name");
      if (typeof name === "string") return name;
      value = Object.getPrototypeOf(value);
    }
  } catch {
    // Proxy prototypes are untrusted too.
  }
  return undefined;
}

const TYPES = new Set([
  "Error",
  "TypeError",
  "RangeError",
  "AbortError",
  "TimeoutError",
  "WalletConnectionError",
  "WalletNotConnectedError",
  "WalletSignMessageError",
  "WalletConnectionLostError",
  "NoSolanaAccountError",
]);

/** Fixed categories and bounded numeric codes, never arbitrary wallet text or causes. */
export function identityFailure(error: unknown): Record<string, string> {
  let code = "unknown";
  let errorType = "unknown";
  let failure = "unknown";
  const pending = [error];
  const seen = new Set<unknown>();
  for (let count = 0; pending.length && count < 5; count += 1) {
    const item = pending.pop();
    if (item === null || item === undefined || seen.has(item)) continue;
    seen.add(item);
    const raw = field(item, "code");
    const number =
      typeof raw === "string" && /^-?\d{1,10}$/.test(raw) ? Number(raw) : raw;
    const nextCode =
      typeof number === "number" &&
      Number.isSafeInteger(number) &&
      Math.abs(number) <= 2_147_483_647
        ? String(number)
        : typeof raw === "string" && CODES.has(raw)
          ? raw
          : "unknown";
    if (code === "unknown" && nextCode !== "unknown") code = nextCode;
    const name = errorName(item);
    if (errorType === "unknown" && typeof name === "string" && TYPES.has(name))
      errorType = name;
    const message = field(item, "message");
    const text = typeof message === "string" ? message.slice(0, 300) : "";
    if (
      nextCode === "4001" ||
      nextCode === "ACTION_REJECTED" ||
      /user (reject|denied)|rejected the request|declined/i.test(text)
    )
      failure = "user_rejected";
    else if (failure === "unknown") {
      if (nextCode === "4100") failure = "not_authorized";
      else if (nextCode === "-32603") failure = "provider_internal";
      else if (name === "TimeoutError" || /timed? ?out/i.test(text))
        failure = "timeout";
      else if (name === "AbortError") failure = "aborted";
      else if (name === "NoSolanaAccountError") failure = "no_solana_account";
      else if (nextCode === "SIGN_IN_NOT_RECORDED")
        failure = "verification_failed";
    }
    for (const key of ["data", "error", "cause"]) {
      const nested = field(item, key);
      if (nested !== undefined) pending.push(nested);
    }
  }
  return { error_code: code, error_type: errorType, failure_class: failure };
}

export function identityAttempt(route: string): IdentityAttempt {
  const attempt = {
    ...walletTelemetry(),
    attempt_id: telemetryAttemptId(),
    operation: "siws",
    route,
  };
  startIdentityTrace(attempt);
  return attempt;
}

export function identityProvider(
  attempt: IdentityAttempt,
  wallet?: WalletTelemetryInput
): void {
  Object.assign(attempt, walletTelemetry(wallet));
}

export function identityPhase(
  attempt: IdentityAttempt,
  phase: string,
  fields: Record<string, string> = {}
): void {
  try {
    if (phase !== "failed") attempt.stage = phase;
    identityTracePhase(attempt, phase, fields);
    rumEvent(`wallet_identity_${phase}`, { ...attempt, phase, ...fields });
  } catch {
    // Telemetry cannot alter authentication or its original rejection.
  }
}

/** Shape only. In particular, never read or export the address or signature itself. */
export function identityResponse(value: unknown): Record<string, string> {
  try {
    const kind =
      value === null ? "null" : Array.isArray(value) ? "array" : typeof value;
    return {
      response_kind: kind,
      response_account: typeof field(value, "account"),
      response_address: typeof field(value, "address"),
      response_signature: typeof field(value, "signature"),
      response_signed_message: typeof field(value, "signedMessage"),
    };
  } catch {
    return { response_kind: "unreadable" };
  }
}

export function injectedIdentity(value: unknown): WalletTelemetryInput {
  return field(value, "isPhantom") === true
    ? { name: "Phantom", telemetrySource: "phantom-adapter" }
    : {};
}
